import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import { useState, useEffect, useCallback } from 'react';
import isBefore from 'date-fns/isBefore';
import _orderBy from 'lodash.orderby';
import classNames from 'classnames';
import Link from 'next/link';
import { usePostHog } from 'posthog-js/react';
import Layout from '@/components/Layout';
import ApplicationFormAuth from '@/components/ApplicationForm/ApplicationFormAuth.js';
import ApplicationFormWelcome from '@/components/ApplicationForm/ApplicationFormWelcome.js';
import ApplicationForm from '@/components/ApplicationForm/ApplicationForm.js';
import useCampus from '@/hooks/useCampus.ts';
import useReferral from '@/hooks/useReferral';
import { getApps } from '@/utils/api.js';
import { programStepForNewSession } from '@/components/ApplicationForm/programStep';
import { useProgramStepAssignment } from '@/hooks/useProgramStepAssignment';
import { submitApplicationWithRecovery, FINAL_STEP } from '@/utils/applicationSubmit.ts';
import { identifyApplicant } from '@/lib/analytics';
import { normalizeReferralCode } from '@/utils/referralCode';
import { getInviteToken } from '@/utils/attribution';
import { usePardot } from '@/components/Pardot.js';
import Spinner from '@helios/Spinner';
import styles from '@/components/ApplicationForm/ApplicationForm.module.scss';
import { allSites, hasHeaderTicker } from '@/utils/domains.js';
import employers from '@/utils/employers.json';
export const getStaticPaths = async () => {
  return {
    paths: allSites.map(site => ({
      params: {
        site
      }
    })),
    fallback: false
  };
};
export async function getStaticProps({
  params
}) {
  return {
    props: {
      site: params.site
    }
  };
}

/**
 * Resolves a new application's program-step variant and reports it once.
 * Mounted only while a new application waits for it, so the flag (and its
 * exposure event) is evaluated only for applicants who start one.
 */
function ProgramStepAssignment(t0) {
  const $ = _c(4);
  const {
    onAssign
  } = t0;
  const assignment = useProgramStepAssignment();
  let t1;
  let t2;
  if ($[0] !== assignment || $[1] !== onAssign) {
    t1 = () => {
      if (assignment) {
        onAssign(assignment);
      }
    };
    t2 = [assignment, onAssign];
    $[0] = assignment;
    $[1] = onAssign;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return null;
}
function Apply(t0) {
  const $ = _c(66);
  const {
    site
  } = t0;
  const isSacramento = site === "sacramento";
  const location = isSacramento ? "sacramento" : "scholars";
  let t1;
  if ($[0] !== site) {
    t1 = employers.find(emp => emp.id === site);
    $[0] = site;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const employer = t1;
  let t2;
  if ($[2] !== location) {
    t2 = {
      location,
      enableLeadQuery: true,
      enableAppsQuery: true
    };
    $[2] = location;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    authenticated,
    termsQuery,
    degreesQuery,
    appsQuery,
    leadQuery,
    userQuery,
    createApp,
    updateApp,
    insertPreferredClassSchedule,
    logout
  } = useCampus(t2);
  const campaignParams = usePardot();
  const {
    refCode
  } = useReferral();
  const posthog = usePostHog();
  const [appId, setAppId] = useState();
  const [newAppProgramStep, setNewAppProgramStep] = useState();
  const terms = _orderBy(termsQuery.data, "termStartsAt", "asc");
  const degrees = degreesQuery.data;
  const apps = appsQuery.data;
  const lead = leadQuery.data;
  const user = userQuery.data;
  const loading = !termsQuery.isSuccess || !appsQuery.isSuccess || !degreesQuery.isSuccess || !leadQuery.isFetched || !userQuery.isFetched;
  const firstApp = authenticated === true && !loading && !apps.length;
  let openApp;
  let t3;
  if ($[4] !== appId || $[5] !== apps || $[6] !== newAppProgramStep) {
    openApp = apps?.find(app => app.id === appId);
    t3 = programStepForNewSession(appId, openApp, newAppProgramStep);
    $[4] = appId;
    $[5] = apps;
    $[6] = newAppProgramStep;
    $[7] = openApp;
    $[8] = t3;
  } else {
    openApp = $[7];
    t3 = $[8];
  }
  const programStep = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setAppId();
      setNewAppProgramStep();
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const closeForm = t4;
  let children;
  let t5;
  let t6;
  if ($[10] !== firstApp) {
    t5 = () => {
      if (firstApp) {
        setAppId("new");
      }
    };
    t6 = [firstApp];
    $[10] = firstApp;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[13] !== posthog || $[14] !== user) {
    t7 = () => {
      identifyApplicant(posthog, user);
    };
    t8 = [user, posthog];
    $[13] = posthog;
    $[14] = user;
    $[15] = t7;
    $[16] = t8;
  } else {
    t7 = $[15];
    t8 = $[16];
  }
  useEffect(t7, t8);
  let t9;
  if ($[17] !== refCode) {
    t9 = t10 => {
      const manualCode = t10 === undefined ? null : t10;
      const code = refCode?.trim() || manualCode?.trim() || null;
      return code || null;
    };
    $[17] = refCode;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const resolveRefCandidate = t9;
  if (authenticated === false) {
    let t10;
    if ($[19] !== site) {
      t10 = hasHeaderTicker(site);
      $[19] = site;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== employer || $[22] !== t10) {
      t11 = <ApplicationFormAuth employer={employer} photoUnderHeader={t10} />;
      $[21] = employer;
      $[22] = t10;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    children = t11;
  } else {
    if (authenticated === true && !loading) {
      if (apps.length && !appId) {
        let t10;
        if ($[24] !== logout) {
          t10 = () => {
            logout();
            closeForm();
          };
          $[24] = logout;
          $[25] = t10;
        } else {
          t10 = $[25];
        }
        children = <ApplicationFormWelcome apps={apps} terms={terms} employer={employer} onApp={setAppId} onLogout={t10} site={site} />;
      } else {
        if (appId && !programStep) {
          let t10;
          if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
            t10 = <ProgramStepAssignment onAssign={setNewAppProgramStep} />;
            $[26] = t10;
          } else {
            t10 = $[26];
          }
          let t11;
          if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
            t11 = <>{t10}<div className={styles.loader}><Spinner /></div></>;
            $[27] = t11;
          } else {
            t11 = $[27];
          }
          children = t11;
        } else {
          if (appId) {
            let t10;
            if ($[28] !== appId || $[29] !== apps) {
              t10 = t_1 => !apps?.some(a => a.id !== appId && a.termId === t_1.id);
              $[28] = appId;
              $[29] = apps;
              $[30] = t10;
            } else {
              t10 = $[30];
            }
            let t11;
            if ($[31] !== apps.length || $[32] !== logout) {
              t11 = () => {
                if (!apps.length) {
                  logout();
                }
                closeForm();
              };
              $[31] = apps.length;
              $[32] = logout;
              $[33] = t11;
            } else {
              t11 = $[33];
            }
            let t12;
            if ($[34] !== appId || $[35] !== appsQuery || $[36] !== campaignParams || $[37] !== createApp || $[38] !== resolveRefCandidate || $[39] !== updateApp || $[40] !== userQuery) {
              t12 = async values => {
                const manualCode_0 = values.hearAboutCampus === "referred" ? values.referredBy ?? null : null;
                const refCandidate = normalizeReferralCode(resolveRefCandidate(manualCode_0));
                const inviteToken = getInviteToken();
                const params = {
                  ...campaignParams,
                  ...(refCandidate ? {
                    ref: refCandidate
                  } : {}),
                  ...(inviteToken ? {
                    inviteToken
                  } : {})
                };
                const app_0 = await submitApplicationWithRecovery({
                  submit: () => appId === "new" ? createApp({
                    data: values,
                    campaignParams: params
                  }) : updateApp({
                    id: appId,
                    data: values,
                    campaignParams: params
                  }),
                  isFinalStep: values.step === FINAL_STEP,
                  appId,
                  termId: values.termId,
                  getApps: _temp4,
                  onAppsChanged: () => appsQuery.refetch()
                });
                if (appId === "new") {
                  setNewAppProgramStep(assigned => assigned ? {
                    ...assigned,
                    appId: app_0.id
                  } : assigned);
                  setAppId(app_0.id);
                  userQuery.refetch();
                }
                return app_0;
              };
              $[34] = appId;
              $[35] = appsQuery;
              $[36] = campaignParams;
              $[37] = createApp;
              $[38] = resolveRefCandidate;
              $[39] = updateApp;
              $[40] = userQuery;
              $[41] = t12;
            } else {
              t12 = $[41];
            }
            let t13;
            if ($[42] !== logout) {
              t13 = () => {
                logout();
                closeForm();
              };
              $[42] = logout;
              $[43] = t13;
            } else {
              t13 = $[43];
            }
            let t14;
            if ($[44] !== appId || $[45] !== insertPreferredClassSchedule) {
              t14 = async values_0 => {
                await insertPreferredClassSchedule({
                  id: appId,
                  data: values_0
                });
              };
              $[44] = appId;
              $[45] = insertPreferredClassSchedule;
              $[46] = t14;
            } else {
              t14 = $[46];
            }
            children = <ApplicationForm site={site} lead={lead} app={openApp} location={location} programStep={programStep} degrees={degrees} terms={terms.filter(_temp).filter(_temp2).slice(0, 2).filter(t10)} onPrev={t11} onSubmit={t12} onLogout={t13} onSubmitClassTimes={t14} employer={employer} />;
          }
        }
      }
    } else {
      if (loading) {
        let t10;
        if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <div className={styles.loader}><Spinner /></div>;
          $[47] = t10;
        } else {
          t10 = $[47];
        }
        children = t10;
      }
    }
  }
  const t10 = authenticated !== false;
  let t11;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Head><title>College Admissions Application | Campus</title><link rel="canonical" href="https://campus.edu/admissions-application" /><meta httpEquiv="X-UA-Compatible" content="IE=Edge" /><meta name="description" content="Learn more about applying to Campus and complete your application in minutes. No SAT required. See requirements, key dates, and start your application." /><meta name="viewport" content="width=device-width, initial-scale=1" /></Head>;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  const t12 = employer ? styles["page-application__co-brand"] : undefined;
  let t13;
  if ($[49] !== children || $[50] !== t12) {
    t13 = <div className={t12}>{children}</div>;
    $[49] = children;
    $[50] = t12;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  const t14 = !authenticated;
  let t15;
  if ($[52] !== t14) {
    t15 = classNames(styles.legal, {
      [styles["legal--hidden"]]: t14
    });
    $[52] = t14;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Link href="/privacy-policy" className="iubenda-cs-uspr-link">Privacy</Link>;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <a href="#" className="iubenda-cs-preferences-link">Cookie preferences</a>;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t15) {
    t18 = <div className={t15}>{t16}{" "}|{" "}{t17}</div>;
    $[56] = t15;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t13 || $[59] !== t18) {
    t19 = <div id="wrapper">{t11}{t13}{t18}</div>;
    $[58] = t13;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] !== authenticated || $[62] !== site || $[63] !== t10 || $[64] !== t19) {
    t20 = <Layout site={site} headerDisabled={authenticated} footerDisabled={authenticated} lightNav={t10}>{t19}</Layout>;
    $[61] = authenticated;
    $[62] = site;
    $[63] = t10;
    $[64] = t19;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  return t20;
}
function _temp4() {
  return getApps().then(_temp3);
}
function _temp3(t0) {
  const {
    data
  } = t0;
  return data;
}
function _temp2(t_0, idx) {
  return idx === 0 ? t_0 : t_0.seasonValue.startsWith("Fall");
}
function _temp(t) {
  return isBefore(new Date(), new Date(t.applyEndsAt));
}
export default Apply;